import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart, Share2, ChevronRight, ChevronLeft,
  Star, ShoppingCart, Truck, Shield, RotateCcw, Check, Minus, Plus,
  Package, ArrowRight, X, Zap, Award,
} from "lucide-react";

import { AppDispatch, RootState }          from "../../redux/store";
import { listenToProductData }             from "../../redux/productSlice";
import { addToCart }                       from "../../redux/cartSlice";
import { fetchProductReviewsByListing }    from "../../redux/productRatingSlice";
import { listenToCommerceData }            from "../../redux/commercePartnersSlice";
import { CurrencyContext }                 from "../../GLOBAL/CurrencyContext";

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES — one unified color export + flat attribute lists.
   This is now the ONLY source of truth for which category a
   product belongs to (Fashion / Beauty) — we detect that from
   which attribute-group ids actually show up on the product's
   `attributes` map, instead of cross-referencing a categories
   file. That means this component works for any listing, as
   long as its attributes were written using these same groups
   (identical source + shape to AddProducts, so ids line up 1:1).
═══════════════════════════════════════════════════════════ */
import {
  GENERAL_COLOR_ATTRIBUTE_ID,
  productColors,
  Materials,
  Fits,
  Sizes,
  ShoeSizes,
  Patterns,
  Sustainability,
  Brands,
  HeelHeights,
  AgeRanges,
  SkinTypes,
  SkinConcerns,
  SkinTones,
  Undertones,
  Coverage,
  Finish,
  HairTypes,
  HairConcerns,
  FragranceFamilies,
  FragranceConcentrations,
  KeyIngredients,
  BeautyFormats,
  BeautySustainability,
  SpfLevels,
  BeautySizes,
  BeautyBrands,
} from "../../CATEGORIES/ATTRIBUTES/ProductsAttributes";

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — WHITE PAGE · BLACK & WHITE ONLY
═══════════════════════════════════════════════════════════ */
const T = {
  bg:         "#FFFFFF",
  bgSoft:     "#F7F7F7",
  bgMuted:    "#EAEAEA",
  ink:        "#0D0D0D",
  inkMid:     "#262626",
  inkSoft:    "#404040",
  muted:      "#767676",
  mutedSoft:  "#C2C2C2",
  gold:       "#0D0D0D",
  goldLight:  "#0D0D0D",
  red:        "#0D0D0D",
  orange:     "#0D0D0D",
  border:     "rgba(0,0,0,0.15)",
  goldGlow:   "rgba(0,0,0,0.25)",
  redGlow:    "rgba(0,0,0,0.20)",
  orangeGlow: "rgba(0,0,0,0.20)",
  gradH:      "linear-gradient(90deg,#0D0D0D 0%,#3A3A3A 50%,#0D0D0D 100%)",
  gradAnim:   "linear-gradient(90deg,#0D0D0D 0%,#3A3A3A 35%,#0D0D0D 70%,#3A3A3A 100%)",
  gradRadial: "radial-gradient(ellipse at top left,rgba(0,0,0,.05) 0%,transparent 55%)",
};

const GRAD_H = T.gradH;
const GRAD_A = T.gradAnim;

/* ── colour lookup — same fallback chain as ProductCheckout / AddProducts ── */
const getColorAttr = (id?: number) => {
  if (!id) return null;
  const grp = productColors.find(g => g.id === GENERAL_COLOR_ATTRIBUTE_ID);
   return grp?.values?.find(v => v.id === id) ?? null;
};

function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%,`,
    `  rgba(255,255,255,0.55) 0%,`,
    `  rgba(255,255,255,0.10) 28%,`,
    `  transparent 52%),`,
    `radial-gradient(circle at 68% 72%,`,
    `  rgba(0,0,0,0.30) 0%,`,
    `  transparent 55%),`,
    `radial-gradient(circle at 50% 50%,`,
    `  ${hex} 0%,`,
    `  ${hex} 100%)`,
  ].join(" ");
}

/* ── size lookup — searches every size pool a product's variants could draw from ── */
const getSizeName = (id?: number | null) => {
  if (!id) return null;
  return (
    Sizes.find(s => s.id === id)?.name ??
    ShoeSizes.find(s => s.id === id)?.name ??
    BeautySizes.find(s => s.id === id)?.name ??
    null
  );
};

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTE GROUPS — Fashion & Beauty
   (kept in lockstep with AddProducts.tsx so group ids / value ids
   always match what's stored on product.attributes)
═══════════════════════════════════════════════════════════ */
const FASHION_ATTR_GROUPS: AttrGroupDef[] = [
  { id: 90101, name: "Material",       values: Materials,      multiSelect: true },
  { id: 90102, name: "Fit",            values: Fits,           optional: true },
  { id: 90103, name: "Size",           values: Sizes,          multiSelect: true, optional: true },
  { id: 90104, name: "Shoe Size",      values: ShoeSizes,      multiSelect: true, optional: true },
  { id: 90105, name: "Pattern",        values: Patterns,       optional: true },
  { id: 90106, name: "Sustainability", values: Sustainability, multiSelect: true, optional: true },
  { id: 90107, name: "Brand",          values: Brands,         optional: true },
  { id: 90108, name: "Heel Height",    values: HeelHeights,    optional: true },
  { id: 90109, name: "Age Range",      values: AgeRanges,      optional: true },
];

const BEAUTY_ATTR_GROUPS: AttrGroupDef[] = [
  { id: 90201, name: "Skin Type",        values: SkinTypes,               multiSelect: true },
  { id: 90202, name: "Skin Concern",     values: SkinConcerns,            multiSelect: true, optional: true },
  { id: 90203, name: "Skin Tone",        values: SkinTones,               optional: true },
  { id: 90204, name: "Undertone",        values: Undertones,              optional: true },
  { id: 90205, name: "Coverage",         values: Coverage,                optional: true },
  { id: 90206, name: "Finish",           values: Finish,                  optional: true },
  { id: 90207, name: "Hair Type",        values: HairTypes,               multiSelect: true, optional: true },
  { id: 90208, name: "Hair Concern",     values: HairConcerns,            multiSelect: true, optional: true },
  { id: 90209, name: "Fragrance Family", values: FragranceFamilies,       optional: true },
  { id: 90210, name: "Concentration",    values: FragranceConcentrations, optional: true },
  { id: 90211, name: "Key Ingredients",  values: KeyIngredients,          multiSelect: true, optional: true },
  { id: 90212, name: "Format",           values: BeautyFormats,           optional: true },
  { id: 90213, name: "Sustainability",   values: BeautySustainability,    multiSelect: true, optional: true },
  { id: 90214, name: "SPF Level",        values: SpfLevels,               optional: true },
  { id: 90215, name: "Size",             values: BeautySizes,             optional: true },
  { id: 90216, name: "Brand",            values: BeautyBrands,            optional: true },
];

interface AttrGroupDef {
  id: number;
  name: string;
  values: { id: number; name: string; icon?: React.ReactNode }[];
  multiSelect?: boolean;
  optional?: boolean;
}

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{display:none;}
  button{font-family:inherit;}

  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes floatUp{0%{transform:translateY(0)}50%{transform:translateY(-4px)}100%{transform:translateY(0)}}

  .sw{width:32px;height:32px;border-radius:50%;cursor:pointer;border:2px solid rgba(0,0,0,.10);transition:all .18s;flex-shrink:0;box-shadow:0 2px 8px rgba(0,0,0,.12);}
  .sw:hover{transform:scale(1.14);border-color:rgba(0,0,0,.5);}
  .sw.on{border:2.5px solid ${T.orange};box-shadow:0 0 0 2px #fff,0 0 0 4px ${T.orange};transform:scale(1.22);}

  .sz{padding:9px 20px;border-radius:999px;border:1.5px solid rgba(0,0,0,.28);background:rgba(0,0,0,.05);color:${T.muted};font-size:12px;font-weight:700;cursor:pointer;transition:all .18s;letter-spacing:.04em;}
  .sz:hover{border-color:rgba(0,0,0,.6);color:${T.inkMid};background:rgba(0,0,0,.06);}
  .sz.on{background:${GRAD_H};background-size:200% 100%;animation:gradSpin 3s ease infinite;border-color:transparent;color:#fff;box-shadow:0 3px 14px rgba(0,0,0,.38);}

  .spec-row{display:flex;padding:10px 16px;transition:background .15s;}
  .spec-row:hover{background:rgba(0,0,0,.05);}

  .ab{flex:1;padding:15px 0;border-radius:14px;font-size:11px;font-weight:800;letter-spacing:.18em;cursor:pointer;transition:all .22s;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:8px;}
  .ab.outline{background:transparent;border:1.5px solid rgba(0,0,0,.40);color:${T.inkMid};}
  .ab.outline:hover{background:rgba(0,0,0,.07);border-color:rgba(0,0,0,.65);box-shadow:0 0 18px rgba(0,0,0,.15);}
  .ab.fill{background:${GRAD_H};background-size:200% 100%;animation:gradSpin 3s ease infinite;border:none;color:#fff;box-shadow:0 6px 28px rgba(0,0,0,.35);}
  .ab.fill:hover{transform:translateY(-2px);box-shadow:0 12px 36px rgba(0,0,0,.45);}
  .ab:disabled{background:rgba(0,0,0,.05)!important;color:#C4B8A8!important;border-color:transparent!important;cursor:not-allowed!important;animation:none!important;box-shadow:none!important;}

  .th{border-radius:10px;overflow:hidden;cursor:pointer;border:1.5px solid rgba(0,0,0,.15);transition:border-color .15s,transform .15s;background:#F7F7F7;}
  .th:hover{border-color:rgba(0,0,0,.45);transform:scale(1.04);}
  .th.on{border-color:${T.orange};box-shadow:0 0 0 1px ${T.orange},0 4px 14px rgba(0,0,0,.22);}

  .rev-card{border-radius:16px;padding:18px;background:#F7F7F7;border:1px solid rgba(0,0,0,.14);transition:border-color .2s,box-shadow .2s;margin-bottom:12px;}
  .rev-card:hover{border-color:rgba(0,0,0,.28);box-shadow:0 4px 20px rgba(0,0,0,.08);}
`;

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" });

/* ── Stars ── */
const Stars: React.FC<{ rating:number; size?:number; count?:number }> = ({ rating, size=13, count }) => (
  <div style={{ display:"flex", alignItems:"center", gap:3 }}>
    {[1,2,3,4,5].map(i => (
      <span key={i} style={{ position:"relative", display:"inline-block", width:size, height:size }}>
        <Star size={size} color={T.mutedSoft} fill={T.mutedSoft}/>
        {i <= Math.floor(rating)
          ? <span style={{ position:"absolute", inset:0 }}><Star size={size} color={T.goldLight} fill={T.goldLight}/></span>
          : i-1 < rating && rating < i
          ? <span style={{ position:"absolute", inset:0, overflow:"hidden", width:`${(rating-(i-1))*100}%` }}><Star size={size} color={T.goldLight} fill={T.goldLight}/></span>
          : null}
      </span>
    ))}
    {count !== undefined && <span style={{ fontSize:11, color:T.muted, marginLeft:4 }}>({count}+)</span>}
  </div>
);

/* ── Rating bar ── */
const RatingBar: React.FC<{ label:string; value:number; total?:number }> = ({ label, value, total }) => {
  const pct = total && total > 0 ? Math.round((value/total)*100) : Math.round((value/5)*100);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
      <span style={{ fontSize:11, color:T.muted, width:80, flexShrink:0 }}>{label}</span>
      <div style={{ flex:1, height:4, background:T.bgMuted, overflow:"hidden", borderRadius:2 }}>
        <motion.div initial={{ width:0 }} animate={{ width:`${pct}%` }} transition={{ duration:.8, ease:"easeOut" }}
          style={{ height:"100%", background:GRAD_H, borderRadius:2 }}/>
      </div>
      <span style={{ fontSize:11, fontWeight:700, color:T.ink, width:28, textAlign:"right" }}>
        {typeof total === "number" ? `${pct}%` : value.toFixed(1)}
      </span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const ProductDetails: React.FC = () => {
  const { listingId: paramId } = useParams<{ listingId:string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { rate: fxRate, symbol } = useContext(CurrencyContext);

  const authUser       = useSelector((s:RootState) => s.auth.user);
  const userId         = authUser?.userId;
  const allUsers       = useSelector((s:RootState) => s.auth.allUsers);
  const cartItems      = useSelector((s:RootState) => s.cart.items);
  const product        = useSelector((s:RootState) => s.products.currentProduct);
  const reviews        = useSelector((s:RootState) => paramId ? s.productRatings.reviews[paramId] ?? [] : []);

  const [activeId,   setActiveId]   = useState(paramId ?? "");
  const [selColor,   setSelColor]   = useState<number|null>(null);
  const [selSize,    setSelSize]    = useState<number|null>(null);
  const [qty,        setQty]        = useState(1);
  const [imgIndex,   setImgIndex]   = useState(0);
  const [modalIndex, setModalIndex] = useState<number|null>(null);
  const [wished,     setWished]     = useState(false);
  const [shared,     setShared]     = useState(false);
  const [isMobile,   setIsMobile]   = useState(window.innerWidth <= 768);
  const [activeTab,  setActiveTab]  = useState<"specs"|"reviews">("specs");
  const [addedFlash, setAddedFlash] = useState(false);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!activeId) return;
    dispatch(listenToProductData(activeId));
    dispatch(fetchProductReviewsByListing(activeId));
    setSelColor(null); setSelSize(null); setQty(1); setImgIndex(0);
    window.scrollTo({ top:0, behavior:"smooth" });
  }, [activeId, dispatch]);

  useEffect(() => {
    if ((product as any)?.brandId) dispatch(listenToCommerceData((product as any).brandId));
  }, [product, dispatch]);

  /* ── variant detection ── */
  const variantMode = useMemo(() => {
    if (!product?.variants || Object.keys(product.variants).length === 0) return null;
    if (Object.values(product.variants).some((v:any) => Array.isArray(v.sizeIds) && v.sizeIds.length > 0)) return "fashion";
    if (Object.values(product.variants).some((v:any) => typeof v.imageUrl === "string")) return "beauty";
    return null;
  }, [product]);

  const colourIds   = variantMode ? Object.keys(product?.variants ?? {}).map(Number) : [];
  const hasVariants = colourIds.length > 0;
  const sizeIds: number[] = selColor ? (product?.variants?.[selColor]?.sizeIds ?? []) : [];
  const variantImg  = selColor ? (product?.variants?.[selColor]?.imageUrl ?? null) : null;

  const photos: string[] = useMemo(() => [
    ...(variantImg ? [variantImg] : []),
    ...(product?.photos ?? []).filter(p => p !== variantImg),
  ], [product, variantImg]);

  const media = useMemo(() => [
    ...(product?.photos ?? []).map(url => ({ type:"image" as const, url })),
    ...(product?.videoUrl ? [{ type:"video" as const, url: product.videoUrl }] : []),
  ], [product]);

  /* ── product group (Fashion / Beauty) — detected directly from the
     attribute ids actually present on product.attributes, by checking
     how many of the ids overlap with each group's known attribute ids.
     No categories file needed — this works for any listing as long as
     its attributes were written using the same ProductsAttributes ids
     AddProducts uses. Whichever group has more matches wins. ── */
  const groupName = useMemo(() => {
    const attrs = (product as any)?.attributes;
    if (!attrs) return null;

    const attrKeys = Object.keys(attrs).map(Number);
    const fashionHits = FASHION_ATTR_GROUPS.filter(g => attrKeys.includes(g.id)).length;
    const beautyHits  = BEAUTY_ATTR_GROUPS.filter(g => attrKeys.includes(g.id)).length;

    if (fashionHits === 0 && beautyHits === 0) return null;
    return fashionHits >= beautyHits ? "Fashion" : "Beauty";
  }, [product]);

  const isFashion = groupName === "Fashion";
  const isBeauty  = groupName === "Beauty";
  const activeAttrGroups: AttrGroupDef[] = isFashion ? FASHION_ATTR_GROUPS : isBeauty ? BEAUTY_ATTR_GROUPS : [];

  /* ── specs: driven entirely by whichever attribute groups were
     detected above (same groups AddProducts writes to product.attributes) ── */
  const specsData = useMemo(() => {
    const attrs = (product as any)?.attributes;
    if (!attrs) return [];

    const rows: { group:{ id:number; name:string }; values:{ id:number; name:string }[] }[] = [];

    activeAttrGroups.forEach(group => {
      const vid = attrs[group.id];
      if (vid === undefined) return;
      if (Array.isArray(vid)) {
        const values = group.values.filter((v:any) => (vid as number[]).includes(v.id));
        if (values.length) rows.push({ group, values });
      } else if (typeof vid === "number") {
        const value = group.values.find((v:any) => v.id === vid);
        if (value) rows.push({ group, values: [value] });
      }
    });

    return rows;
  }, [product, activeAttrGroups]);

  /* ── pricing ── */
  const fx  = fxRate ?? 1;
  const sym = symbol ?? "€";
  const price     = product ? `${sym}${((product.rate ?? 0)*fx).toFixed(2)}` : "";
  const origPrice = (product as any)?.compareAtRate
    ? `${sym}${((product as any).compareAtRate*fx).toFixed(2)}` : null;
  const discount  = (product as any)?.compareAtRate && (product as any).compareAtRate > (product?.rate ?? 0)
    ? Math.round((((product as any).compareAtRate-(product!.rate!))/(product as any).compareAtRate)*100) : null;

  const totalQty   = cartItems.reduce((s,i) => s+i.qty, 0);
  const totalPrice = cartItems.reduce((s,i) => s+i.qty*i.rate, 0);

  const canBuy = !hasVariants
    || (variantMode === "fashion" && selColor !== null && (sizeIds.length === 0 || selSize !== null))
    || (variantMode === "beauty"  && selColor !== null);

  const handleAddToCart = () => {
    if (!product || !canBuy) return;
    dispatch(addToCart({
      listingId: product.listingId,
      name:    product.name ?? "",
      rate:   product.rate ?? 0,
      qty,
      photo:   variantImg ?? photos[0],
      colorId: selColor ?? undefined,
      sizeId:  selSize  ?? undefined,
      hostId:  product.hostId,
      brandId: (product as any).brandId,
    }));
    setAddedFlash(true);
    setTimeout(() => setAddedFlash(false), 1600);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate(`/ProductCheckout/${userId}`, { state:{ userId, totalQty, totalPrice, items:cartItems } });
  };

  const handleShare = () => {
    navigator.share?.({ title: product?.name, url: window.location.href }).catch(() => {});
    setShared(true); setTimeout(() => setShared(false), 2000);
  };

  /* ── loading ── */
  if (!product) return (
    <div style={{ minHeight:"100vh", background:"#FFFFFF", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16 }}>
      <style>{CSS}</style>
      <div style={{ width:48, height:48, borderRadius:"50%", background:GRAD_H, backgroundSize:"200% 100%", animation:"gradSpin 1.5s ease infinite", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={{ width:36, height:36, borderRadius:"50%", background:"#fff" }}/>
      </div>
      <span style={{ fontSize:11, color:T.muted, letterSpacing:"0.18em", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>LOADING PRODUCT…</span>
    </div>
  );

  /* ────────────────────────────────────────
     SELECTORS
  ──────────────────────────────────────── */
  const ColourSelector = () => colourIds.length > 0 ? (
    <div style={{ marginBottom:22 }}>
      <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.18em", color:T.muted, marginBottom:12, display:"flex", alignItems:"center", gap:6 }}>
        <span>COLOUR</span>
        {selColor && (
          <span style={{ fontWeight:600, color:T.gold, letterSpacing:"0.06em", textTransform:"none", fontSize:12 }}>
            — {getColorAttr(selColor)?.name ?? selColor}
          </span>
        )}
      </div>
<div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(72px,1fr))", gap:10 }}>
      {colourIds.map(cId => {
  const col = getColorAttr(cId);
  const hex = col?.hexCodes?.[0];
  const isSel = selColor === cId;
  const isMulti = col?.name === "Multicolor";
  const isTransparent = col?.name === "Transparent";
  const isLight = hex && ["#FFFFFF","#FFFFF0","#FFFDD0","#FAFAFA","#E6E6FA",
    "#F5F5DC","#87CEEB","#98FF98","#CDDC39","#FFEB3B","#FFC107","#FFDB58"].includes(hex.toUpperCase());
  return (
    <button key={cId}
      onClick={() => { setSelColor(cId); setSelSize(null); setImgIndex(0); }}
      style={{ background:"none", border:"none", padding:0, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}
      title={col?.name ?? String(cId)}
    >
      <div style={{ position:"relative", width:40, height:40 }}>
        {isMulti ? (
          <div style={{ width:40, height:40, borderRadius:"50%",
            background:"conic-gradient(#e53935 0deg 51deg,#FF9800 51deg 102deg,#FFEB3B 102deg 153deg,#228B22 153deg 204deg,#4169E1 204deg 255deg,#9C27B0 255deg 306deg,#e53935 306deg 360deg)",
            boxShadow: isSel
              ? "inset -6px -4px 12px rgba(0,0,0,.28),inset 6px 5px 12px rgba(255,255,255,.18),0 0 0 2.5px #0D0D0D,0 4px 14px rgba(0,0,0,.22)"
              : "inset -6px -4px 12px rgba(0,0,0,.28),inset 6px 5px 12px rgba(255,255,255,.18),0 4px 14px rgba(0,0,0,.22)",
            outline: isSel ? "2.5px solid #0D0D0D" : "none", outlineOffset:3 }}/>
        ) : isTransparent ? (
          <div style={{ width:40, height:40, borderRadius:"50%",
            background:"repeating-conic-gradient(#d0d0d0 0% 25%,#f8f8f8 0% 50%) 0 0/10px 10px",
            border:"1.5px solid rgba(0,0,0,0.10)",
            outline: isSel ? "2.5px solid #0D0D0D" : "none", outlineOffset:3 }}/>
        ) : (
          <div style={{ width:40, height:40, borderRadius:"50%",
            background: hex ? sphere3D(hex) : sphere3D(T.bgMuted),
            boxShadow: isLight
              ? `inset -5px -4px 10px rgba(0,0,0,.12),inset 4px 4px 8px rgba(255,255,255,.6),0 1px 0 rgba(0,0,0,.15),0 4px 12px rgba(0,0,0,.12)${isSel ? ",0 0 0 2.5px #0D0D0D" : ""}`
              : `inset -6px -5px 12px rgba(0,0,0,.30),inset 5px 5px 10px rgba(255,255,255,.18),0 5px 18px rgba(0,0,0,.20)${isSel ? ",0 0 0 2.5px #0D0D0D" : ""}`,
            outline: isSel ? "2.5px solid #0D0D0D" : "none", outlineOffset:3,
            transition:"transform .18s", transform:"scale(1)" }}/>
        )}
        {isSel && (
          <div style={{ position:"absolute", bottom:-3, right:-3, width:16, height:16, borderRadius:"50%",
            background:"#0D0D0D", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 1px 4px rgba(0,0,0,.3)" }}>
            <Check size={9} color="#fff" strokeWidth={3}/>
          </div>
        )}
      </div>
      <span style={{ fontSize:10, fontWeight: isSel?700:500, color: isSel?"#0D0D0D":"#767676", textAlign:"center", maxWidth:68, wordBreak:"break-word" as const, lineHeight:1.2, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
        {col?.name ?? String(cId)}
      </span>
    </button>
  );
})}

      </div>
    </div>
  ) : null;

  const SizeSelector = () => sizeIds.length > 0 ? (
    <div style={{ marginBottom:22 }}>
      <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.18em", color:T.muted, marginBottom:12, display:"flex", alignItems:"center", gap:6 }}>
        <span>SIZE</span>
        {selSize && (
          <span style={{ fontWeight:600, color:T.gold, letterSpacing:"0.06em", textTransform:"none", fontSize:12 }}>
            — {getSizeName(selSize)}
          </span>
        )}
      </div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
        {sizeIds.map(sId => (
          <button key={sId} className={`sz${selSize === sId ? " on" : ""}`} onClick={() => setSelSize(sId)}>
            {getSizeName(sId)}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  /* ── specs table ── */
  const SpecsTable = () => (
    <>
      {specsData.length > 0 && (
        <div style={{ border:`1px solid ${T.border}`, borderRadius:14, overflow:"hidden", marginBottom:20 }}>
          {specsData.map(({ group, values }, i) => (
            <div key={group.id} className="spec-row"
              style={{ borderTop: i > 0 ? `1px solid ${T.border}` : "none", background: i%2===0 ? "#FFFFFF" : T.bgSoft }}>
              <span style={{ width:150, fontSize:11, color:T.muted, flexShrink:0, fontWeight:600 }}>{group.name}</span>
              <span style={{ fontSize:11, color:T.ink }}>{values.map((v:any) => v.name).join(", ")}</span>
            </div>
          ))}
          {(product as any)?.description && (
            <div style={{ padding:"14px 16px", borderTop:`1px solid ${T.border}`, background:T.bgSoft }}>
              <span style={{ fontSize:11, color:T.muted, display:"block", marginBottom:6, fontWeight:600 }}>Description</span>
              <span style={{ fontSize:13, color:T.inkSoft, lineHeight:1.9 }}>{(product as any).description}</span>
            </div>
          )}
        </div>
      )}
      {specsData.length === 0 && (product as any)?.description && (
        <div style={{ border:`1px solid ${T.border}`, borderRadius:14, padding:"14px 16px", marginBottom:20, background:T.bgSoft }}>
          <span style={{ fontSize:11, color:T.muted, display:"block", marginBottom:6, fontWeight:600 }}>Description</span>
          <span style={{ fontSize:13, color:T.inkSoft, lineHeight:1.9 }}>{(product as any).description}</span>
        </div>
      )}
    </>
  );

  /* ── reviews ── */
  const ReviewsSection: React.FC<{ mob?:boolean }> = ({ mob }) => {
    const [showAll, setShowAll] = useState(false);
    const displayed = showAll ? reviews : reviews.slice(0, 3);
    const avg = reviews.length ? reviews.reduce((s:number,r:any) => s+r.rating, 0)/reviews.length : 0;
    return (
      <section style={{ padding: mob ? "0 16px 28px" : "0 0 36px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, paddingTop:18, borderTop:`1px solid ${T.border}` }}>
          <h3 style={{ fontSize: mob?18:22, fontWeight:700, color:T.ink, letterSpacing:-.3, fontFamily:"'Cormorant Garamond',serif" }}>
            Reviews {reviews.length > 0 ? `(${reviews.length})` : ""}
          </h3>
          {reviews.length > 3 && (
            <button onClick={() => setShowAll(v => !v)}
              style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:T.muted, display:"flex", alignItems:"center", gap:3 }}>
              {showAll?"Show less":"View all"}<ArrowRight size={11}/>
            </button>
          )}
        </div>
        {reviews.length > 0 && (
          <div style={{ background:T.bgSoft, border:`1px solid ${T.border}`, padding:"20px 22px", marginBottom:18, borderRadius:18 }}>
            <div style={{ display:"flex", gap:28, flexWrap:"wrap" }}>
              <div style={{ textAlign:"center", minWidth:84 }}>
                <div style={{ fontSize:52, fontWeight:900, lineHeight:1, fontFamily:"'Cormorant Garamond',serif",
                  background:GRAD_H, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite",
                  WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
                  {avg.toFixed(1)}
                </div>
                <Stars rating={avg} size={14}/>
                <div style={{ fontSize:10, color:T.muted, marginTop:5 }}>{reviews.length} reviews</div>
              </div>
              <div style={{ flex:1, minWidth:160 }}>
                {[5,4,3,2,1].map(star => {
                  const cnt = (reviews as any[]).filter(r => Math.round(r.rating)===star).length;
                  return <RatingBar key={star} label={`${star} star${star>1?"s":""}`} value={cnt} total={reviews.length}/>;
                })}
              </div>
            </div>
          </div>
        )}
        {(displayed as any[]).map((rev:any, i:number) => {
          const user = (allUsers as any[]).find(u => u.userId===rev.guestId);
          return (
            <motion.div key={i} initial={{ opacity:0,y:12 }} animate={{ opacity:1,y:0 }} transition={{ delay:i*.07 }}
              className="rev-card">
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                <div style={{ width:36, height:36, borderRadius:"50%", background:T.bgMuted, overflow:"hidden", flexShrink:0, border:`1px solid ${T.border}` }}>
                  {user?.profilePicture
                    ? <img src={user.profilePicture} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                    : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800,
                        background:GRAD_H, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
                        {(user?.firstName?.[0]??"U").toUpperCase()}
                      </div>
                  }
                </div>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:T.ink }}>{user?.firstName??"Verified Buyer"}</div>
                  <div style={{ fontSize:10, color:T.muted }}>{fmtDate(rev.dateCreated)}</div>
                </div>
                <div style={{ marginLeft:"auto" }}><Stars rating={rev.rating} size={12}/></div>
              </div>
              {rev.title && <div style={{ fontSize:13, fontWeight:700, color:T.ink, marginBottom:5 }}>{rev.title}</div>}
              <div style={{ fontSize:13, color:T.muted, lineHeight:1.75 }}>{rev.reviewText}</div>
              {Array.isArray(rev.images) && rev.images.length > 0 && (
                <div style={{ display:"flex", gap:8, marginTop:10 }}>
                  {rev.images.map((img:string,j:number) => (
                    <img key={j} src={img} alt="" style={{ width:68, height:68, objectFit:"cover", border:`1px solid ${T.border}`, borderRadius:10 }}/>
                  ))}
                </div>
              )}
            </motion.div>
          );
        })}
        {reviews.length === 0 && (
          <div style={{ padding:"28px 0", textAlign:"center" }}>
            <Star size={30} color={T.mutedSoft} style={{ marginBottom:10 }}/>
            <p style={{ fontSize:12, color:T.muted }}>No reviews yet — be the first</p>
          </div>
        )}
      </section>
    );
  };

  const trust = [
    { icon:<Truck size={14}/>,     title:"Free delivery over €300",  sub:"Estimated 3–7 business days" },
    { icon:<RotateCcw size={14}/>, title:"30-day free returns",       sub:"Hassle-free return policy"   },
    { icon:<Shield size={14}/>,    title:"Verified listing",          sub:"Every listing is reviewed"   },
    { icon:<Award size={14}/>,     title:"Authenticity guaranteed",   sub:"100% genuine products"       },
  ];

  /* ════════════════════════════════════════════
     MOBILE LAYOUT
  ════════════════════════════════════════════ */
  const MobileLayout = () => (
    <div style={{ paddingBottom:110 }}>
      {/* hero image */}
      <div style={{ position:"relative", background:T.bgSoft, aspectRatio:"4/5", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, background:T.gradRadial, zIndex:0, pointerEvents:"none" }}/>
        <AnimatePresence mode="wait">
          <motion.img key={imgIndex} src={photos[imgIndex]} alt={product.name}
            initial={{ opacity:0, scale:1.04 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0 }}
            transition={{ duration:.35 }}
            style={{ width:"100%", height:"100%", objectFit:"contain", padding:18, position:"relative", zIndex:1 }}/>
        </AnimatePresence>

        {photos.length > 1 && (<>
          <button onClick={() => setImgIndex(i => (i-1+photos.length)%photos.length)}
            style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,.88)", border:`1px solid ${T.border}`, width:34, height:34, borderRadius:"50%", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", zIndex:2, backdropFilter:"blur(6px)" }}>
            <ChevronLeft size={16} color={T.ink}/>
          </button>
          <button onClick={() => setImgIndex(i => (i+1)%photos.length)}
            style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,.88)", border:`1px solid ${T.border}`, width:34, height:34, borderRadius:"50%", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", zIndex:2, backdropFilter:"blur(6px)" }}>
            <ChevronRight size={16} color={T.ink}/>
          </button>
        </>)}

        <div style={{ position:"absolute", bottom:12, left:0, right:0, display:"flex", justifyContent:"center", gap:5, zIndex:2 }}>
          {photos.map((_,i) => (
            <div key={i} onClick={() => setImgIndex(i)}
              style={{ width:i===imgIndex?18:6, height:6, borderRadius:3, background:i===imgIndex?T.orange:"rgba(0,0,0,.22)", cursor:"pointer", transition:"all .2s" }}/>
          ))}
        </div>

        {discount && (
          <div style={{ position:"absolute", top:14, left:14, zIndex:3 }}>
            <div style={{ background:T.red, color:"#fff", fontSize:11, fontWeight:800, padding:"4px 10px", borderRadius:999 }}>
              -{discount}%
            </div>
          </div>
        )}

        <div style={{ position:"absolute", top:14, right:14, display:"flex", flexDirection:"column", gap:8, zIndex:3 }}>
          <motion.button whileTap={{ scale:.88 }} onClick={() => setWished(v => !v)}
            style={{ background:"rgba(255,255,255,.9)", border:`1px solid ${wished?T.red:T.border}`, width:38, height:38, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
            <Heart size={16} color={wished?T.red:T.muted} fill={wished?T.red:"none"}/>
          </motion.button>
          <motion.button whileTap={{ scale:.88 }} onClick={handleShare}
            style={{ background:"rgba(255,255,255,.9)", border:`1px solid ${T.border}`, width:38, height:38, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
            {shared?<Check size={15} color={T.gold}/>:<Share2 size={15} color={T.muted}/>}
          </motion.button>
        </div>
      </div>

      {/* thumb strip */}
      {photos.length > 1 && (
        <div style={{ display:"flex", gap:8, padding:"12px 16px", overflowX:"auto", background:"#fff", borderBottom:`1px solid ${T.border}` }}>
          {photos.map((src,i) => (
            <div key={i} className={`th${i===imgIndex?" on":""}`} onClick={() => setImgIndex(i)}
              style={{ width:54, height:62, flexShrink:0 }}>
              <img src={src} alt="" style={{ width:"100%", height:"100%", objectFit:"contain", padding:4 }}/>
            </div>
          ))}
        </div>
      )}

      {/* info */}
      <div style={{ padding:"20px 16px 0", background:"#fff" }}>
        <h1 style={{ fontSize:22, fontWeight:700, color:T.ink, lineHeight:1.3, marginBottom:10, letterSpacing:-.4, fontFamily:"'Cormorant Garamond',serif" }}>
          {product.name}
        </h1>
        <div style={{ display:"flex", alignItems:"baseline", gap:10, marginBottom:18 }}>
          <span style={{ fontSize:28, fontWeight:900, background:GRAD_H, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
            {price}
          </span>
          {origPrice && <span style={{ fontSize:14, color:T.muted, textDecoration:"line-through" }}>{origPrice}</span>}
          {discount && <span style={{ fontSize:11, fontWeight:800, background:T.red, color:"#fff", padding:"2px 8px", borderRadius:999 }}>SAVE {discount}%</span>}
        </div>

        <ColourSelector/>
        <SizeSelector/>

        {/* qty */}
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:22 }}>
          <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.16em", color:T.muted }}>QTY</span>
          <div style={{ display:"flex", border:`1.5px solid ${T.border}`, borderRadius:12, overflow:"hidden", background:T.bgSoft }}>
            <button onClick={() => setQty(q => Math.max(1,q-1))} style={{ width:40,height:40,border:"none",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.orange }}><Minus size={14}/></button>
            <div style={{ width:44,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:800,color:T.ink,borderLeft:`1px solid ${T.border}`,borderRight:`1px solid ${T.border}` }}>{qty}</div>
            <button onClick={() => setQty(q => q+1)} style={{ width:40,height:40,border:"none",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.orange }}><Plus size={14}/></button>
          </div>
        </div>

        {/* tab switcher */}
        <div style={{ display:"flex", gap:4, marginBottom:20, background:T.bgSoft, padding:4, borderRadius:12, border:`1px solid ${T.border}` }}>
          {(["specs","reviews"] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ flex:1, padding:"9px 0", borderRadius:9, border:"none", cursor:"pointer", fontWeight:700, fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase" as const,
                background: activeTab===tab ? GRAD_H : "transparent",
                backgroundSize:"200% 100%",
                animation: activeTab===tab ? "gradSpin 3s ease infinite" : "none",
                color: activeTab===tab ? "#fff" : T.muted,
                transition:"all .2s" }}>
              {tab}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity:0,y:6 }} animate={{ opacity:1,y:0 }} exit={{ opacity:0 }}>
            {activeTab==="specs" ? <SpecsTable/> : <ReviewsSection mob/>}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* trust */}
      <div style={{ margin:"0 16px 16px", border:`1px solid ${T.border}`, borderRadius:14, overflow:"hidden" }}>
        {trust.map((item,i) => (
          <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start", padding:"11px 14px",
            borderTop: i>0?`1px solid ${T.border}`:"none",
            background: i%2===0?"#FFFFFF":T.bgSoft }}>
            <span style={{ color:T.orange, marginTop:1 }}>{item.icon}</span>
            <div>
              <div style={{ fontSize:12, color:T.ink, fontWeight:600 }}>{item.title}</div>
              <div style={{ fontSize:10, color:T.muted, marginTop:1 }}>{item.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* sticky CTA */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:"rgba(255,255,255,.95)", borderTop:`1px solid ${T.border}`, padding:"12px 16px", display:"flex", gap:10, zIndex:200, backdropFilter:"blur(12px)" }}>
        <AnimatePresence>
          {addedFlash && (
            <motion.div initial={{ opacity:0,y:8 }} animate={{ opacity:1,y:0 }} exit={{ opacity:0 }}
              style={{ position:"absolute", top:-44, left:"50%", transform:"translateX(-50%)", background:T.gold, color:"#fff", fontWeight:800, fontSize:11, padding:"7px 18px", borderRadius:999, letterSpacing:"0.12em", whiteSpace:"nowrap" as const, boxShadow:`0 4px 16px ${T.goldGlow}` }}>
              ✓ ADDED TO CART
            </motion.div>
          )}
        </AnimatePresence>
        <button className="ab outline" onClick={handleAddToCart} disabled={!canBuy}><ShoppingCart size={14}/> ADD TO CART</button>
        <button className="ab fill"    onClick={handleBuyNow}    disabled={!canBuy}><Zap size={14}/> BUY NOW</button>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════
     DESKTOP LAYOUT
  ════════════════════════════════════════════ */
  const DesktopLayout = () => (
    <div style={{ maxWidth:1320, margin:"0 auto", padding:"32px 48px 80px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:26, fontSize:11, color:T.muted, letterSpacing:"0.06em" }}>
        <span style={{ cursor:"pointer" }} onClick={() => navigate(`/ProductsHome/${userId}`)}>Home</span>
        <ChevronRight size={11}/>
        <span style={{ cursor:"pointer" }} onClick={() => navigate(`/ProductsHome/${userId}`)}>Products</span>
        <ChevronRight size={11}/>
        <span style={{ color:T.ink }}>{product.name}</span>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:64, marginBottom:64 }}>
        {/* left: images */}
        <div>
          <div style={{ display:"flex", gap:14 }}>
            <div style={{ display:"flex", flexDirection:"column", gap:10, width:76, flexShrink:0 }}>
              {photos.map((src,i) => (
                <div key={i} className={`th${i===imgIndex?" on":""}`} onClick={() => setImgIndex(i)}
                  style={{ width:76, height:90 }}>
                  <img src={src} alt="" style={{ width:"100%", height:"100%", objectFit:"contain", padding:6 }}/>
                </div>
              ))}
            </div>
            <div style={{ flex:1, position:"relative", background:T.bgSoft, overflow:"hidden", borderRadius:20, border:`1px solid ${T.border}` }}>
              <div style={{ position:"absolute", inset:0, background:T.gradRadial, pointerEvents:"none", zIndex:0 }}/>
              <AnimatePresence mode="wait">
                <motion.img key={imgIndex} src={photos[imgIndex]} alt={product.name}
                  initial={{ opacity:0, scale:1.03 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0 }}
                  transition={{ duration:.35 }}
                  style={{ width:"100%", aspectRatio:"4/5", objectFit:"contain", display:"block", padding:28, position:"relative", zIndex:1 }}/>
              </AnimatePresence>
              {discount && (
                <div style={{ position:"absolute", top:16, left:16, background:T.red, color:"#fff", fontSize:12, fontWeight:800, padding:"5px 12px", borderRadius:999, zIndex:2 }}>
                  -{discount}%
                </div>
              )}
              <div style={{ position:"absolute", top:16, right:16, display:"flex", flexDirection:"column", gap:8, zIndex:2 }}>
                <motion.button whileHover={{ scale:1.1 }} whileTap={{ scale:.88 }} onClick={() => setWished(v => !v)}
                  style={{ background:"rgba(255,255,255,.92)", border:`1px solid ${wished?T.red:T.border}`, width:40, height:40, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                  <Heart size={17} color={wished?T.red:T.muted} fill={wished?T.red:"none"}/>
                </motion.button>
                <motion.button whileHover={{ scale:1.1 }} whileTap={{ scale:.88 }} onClick={handleShare}
                  style={{ background:"rgba(255,255,255,.92)", border:`1px solid ${T.border}`, width:40, height:40, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                  {shared?<Check size={16} color={T.gold}/>:<Share2 size={16} color={T.muted}/>}
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        {/* right: info */}
        <div>
          <h1 style={{ fontSize:30, fontWeight:700, color:T.ink, lineHeight:1.2, marginBottom:16, letterSpacing:-.5, fontFamily:"'Cormorant Garamond',serif" }}>
            {product.name}
          </h1>
          <div style={{ display:"flex", alignItems:"baseline", gap:12, marginBottom:26 }}>
            <span style={{ fontSize:34, fontWeight:900, background:GRAD_H, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
              {price}
            </span>
            {origPrice && <span style={{ fontSize:17, color:T.muted, textDecoration:"line-through" }}>{origPrice}</span>}
            {discount && <span style={{ fontSize:12, fontWeight:800, background:T.red, color:"#fff", padding:"3px 10px", borderRadius:999 }}>SAVE {discount}%</span>}
          </div>

          <div style={{ height:1, background:GRAD_H, backgroundSize:"200% 100%", animation:"gradSpin 4s ease infinite", marginBottom:24, opacity:.3, borderRadius:1 }}/>

          <ColourSelector/>
          <SizeSelector/>

          <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:24 }}>
            <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.16em", color:T.muted }}>QTY</span>
            <div style={{ display:"flex", border:`1.5px solid ${T.border}`, borderRadius:12, overflow:"hidden", background:T.bgSoft }}>
              <button onClick={() => setQty(q => Math.max(1,q-1))} style={{ width:42,height:44,border:"none",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.orange }}><Minus size={15}/></button>
              <div style={{ width:48,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:800,color:T.ink,borderLeft:`1px solid ${T.border}`,borderRight:`1px solid ${T.border}` }}>{qty}</div>
              <button onClick={() => setQty(q => q+1)} style={{ width:42,height:44,border:"none",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.orange }}><Plus size={15}/></button>
            </div>
          </div>

          <div style={{ display:"flex", gap:12, marginBottom:28, position:"relative" }}>
            <AnimatePresence>
              {addedFlash && (
                <motion.div initial={{ opacity:0,y:8 }} animate={{ opacity:1,y:0 }} exit={{ opacity:0 }}
                  style={{ position:"absolute", top:-44, left:"50%", transform:"translateX(-50%)", background:T.gold, color:"#fff", fontWeight:800, fontSize:11, padding:"7px 20px", borderRadius:999, letterSpacing:"0.12em", whiteSpace:"nowrap" as const, boxShadow:`0 4px 16px ${T.goldGlow}` }}>
                  ✓ ADDED TO CART
                </motion.div>
              )}
            </AnimatePresence>
            <button className="ab outline" onClick={handleAddToCart} disabled={!canBuy}><ShoppingCart size={15}/> ADD TO CART</button>
            <button className="ab fill"    onClick={handleBuyNow}    disabled={!canBuy}><Zap size={15}/> BUY NOW</button>
          </div>

          <div style={{ border:`1px solid ${T.border}`, borderRadius:14, overflow:"hidden", marginBottom:24 }}>
            {trust.map((item,i) => (
              <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start", padding:"13px 18px",
                borderTop: i>0?`1px solid ${T.border}`:"none",
                background: i%2===0?"#FFFFFF":T.bgSoft }}>
                <span style={{ color:T.orange, marginTop:1 }}>{item.icon}</span>
                <div>
                  <div style={{ fontSize:12, color:T.ink, fontWeight:600 }}>{item.title}</div>
                  <div style={{ fontSize:10, color:T.muted, marginTop:1 }}>{item.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <SpecsTable/>
        </div>
      </div>

      <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:36 }}>
        <ReviewsSection/>
      </div>
    </div>
  );

  /* ── lightbox ── */
  const LightboxModal = () => (
    <AnimatePresence>
      {modalIndex !== null && media[modalIndex]?.type === "image" && (
        <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
          onClick={() => setModalIndex(null)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.92)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div onClick={e => e.stopPropagation()} style={{ position:"relative", maxWidth:"92vw", maxHeight:"92vh" }}>
            <div style={{ position:"absolute", inset:-2, borderRadius:18, background:GRAD_H, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", zIndex:-1 }}/>
            <img src={media[modalIndex].url} style={{ maxWidth:"100%", maxHeight:"90vh", borderRadius:16, objectFit:"contain", display:"block" }}/>
            <button onClick={() => setModalIndex(null)} style={{ position:"absolute", top:-50, right:0, background:"transparent", border:"none", color:"#fff", cursor:"pointer" }}><X size={34}/></button>
            {modalIndex > 0 && (
              <button onClick={() => setModalIndex(i => i!==null?i-1:i)} style={{ position:"absolute", left:-64, top:"50%", transform:"translateY(-50%)", background:"transparent", border:"none", color:"#fff", cursor:"pointer" }}><ChevronLeft size={50}/></button>
            )}
            {modalIndex < media.length-1 && (
              <button onClick={() => setModalIndex(i => i!==null?i+1:i)} style={{ position:"absolute", right:-64, top:"50%", transform:"translateY(-50%)", background:"transparent", border:"none", color:"#fff", cursor:"pointer" }}><ChevronRight size={50}/></button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ════════════ ROOT RENDER ════════════ */
  return (
    <div style={{ minHeight:"100vh", background:"#FFFFFF", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{CSS}</style>

      {/* sticky nav */}
      <div style={{ padding: isMobile?"12px 16px":"14px 48px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", background:"rgba(255,255,255,.95)", position:"sticky", top:0, zIndex:100, backdropFilter:"blur(12px)" }}>
        <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:GRAD_A, backgroundSize:"300% 100%", animation:"gradSpin 4s linear infinite" }}/>
        <button onClick={() => navigate(-1)}
          style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:11, color:T.muted, letterSpacing:"0.08em" }}>
          <ChevronLeft size={15}/> <span>BACK</span>
        </button>
        {totalQty > 0 && (
          <motion.button initial={{ scale:0 }} animate={{ scale:1 }}
            onClick={() => navigate(`/ProductCheckout/${userId}`, { state:{ userId, totalQty, totalPrice, items:cartItems } })}
            style={{ display:"flex", alignItems:"center", gap:8, background:"rgba(0,0,0,.06)", border:`1px solid rgba(0,0,0,.25)`, padding:"7px 14px", borderRadius:999, cursor:"pointer", color:T.ink, fontSize:11, fontWeight:700, letterSpacing:"0.08em" }}>
            <ShoppingCart size={14}/>
            {isMobile ? totalQty : `${totalQty} item${totalQty>1?"s":""} in cart`}
          </motion.button>
        )}
      </div>

      {isMobile ? <MobileLayout/> : <DesktopLayout/>}

      {isMobile && totalQty > 0 && (
        <motion.div initial={{ scale:0 }} animate={{ scale:1 }}
          onClick={() => navigate(`/ProductCheckout/${userId}`, { state:{ userId, totalQty, totalPrice, items:cartItems } })}
          style={{ position:"fixed", bottom:92, right:20, width:56, height:56, borderRadius:"50%", background:GRAD_H, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", boxShadow:`0 10px 32px ${T.orangeGlow}`, zIndex:150 }}>
          <ShoppingCart size={22} color="#fff"/>
          <span style={{ position:"absolute", top:-5, right:-5, background:T.red, color:"#fff", fontSize:9, fontWeight:900, width:20, height:20, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{totalQty}</span>
        </motion.div>
      )}

      <LightboxModal/>
    </div>
  );
};

export default ProductDetails;